/**
 * Public neighborhood map data — boundaries to draw on the map, and the curated
 * insights shown when a border is clicked. These endpoints are public (no
 * admin gate), a non-sensitive subset of the admin Neighborhood Intelligence.
 */

import { apiRequest } from '../utils/apiClient';

export interface AreaBoundary {
  areaId: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  cityId: string;
  latitude: number | null;
  longitude: number | null;
  /** GeoJSON geometry (Polygon | MultiPolygon), lng/lat order. */
  boundary: GeoJSONGeometry;
  /**
   * District score 0–5, computed nightly from real listing data
   * (backend neighborhoodScoring job). Null = not enough data — the
   * polygon stays neutral; nothing fabricated.
   */
  score: number | null;
}

export interface GeoJSONGeometry {
  type: 'Polygon' | 'MultiPolygon';
  coordinates: number[][][] | number[][][][];
}

export interface KeyCount { key: string; count: number }

export interface AreaInsights {
  areaId: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  cityNameEn: string | null;
  cityNameAr: string | null;
  period: string;
  activeListings: number;
  avgPrice: number;
  byCategory: KeyCount[];
  propertyViews: number;
  areaViews: number;
  favorites: number;
  inquiries: number;
  sold: number;
  rented: number;
  sellingRate: number;
  soldByCategory: KeyCount[];
  /** District score 0–5 (nightly job); null = no honest score yet. */
  score: number | null;
  /** Per-factor breakdown backing the score (each 0–5 or null). */
  scoreComponents: {
    livability: number | null;
    investment: number | null;
    family: number | null;
    safety: number | null;
  } | null;
  /** Active listings the score was computed from. */
  scoreSampleSize: number | null;
}

/** A district/neighborhood autocomplete suggestion (no boundary payload). */
export interface AreaSuggestion {
  id: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  cityId: string;
  latitude: number | null;
  longitude: number | null;
  hasBoundary: boolean;
  activeListingCount: number;
  /** District score 0–5 (nightly job); null/undefined = no score chip shown. */
  score?: number | null;
}

export interface City {
  id: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  latitude: number | null;
  longitude: number | null;
  propertyCount?: number;
}

/** All active cities (for the home + search city selectors). */
export async function fetchCities(): Promise<City[]> {
  const res = await apiRequest<{ data: City[] }>(`/locations/cities`);
  return res.data ?? [];
}

/** District/neighborhood autocomplete by name (EN + AR), optional city scope. */
export async function searchAreas(q: string, cityId?: string, limit = 10): Promise<AreaSuggestion[]> {
  const params = new URLSearchParams({ q });
  if (cityId) params.set('cityId', cityId);
  if (limit) params.set('limit', String(limit));
  const res = await apiRequest<{ data: AreaSuggestion[] }>(`/locations/areas/search?${params.toString()}`);
  return res.data ?? [];
}

/**
 * A combined location autocomplete suggestion — either a CITY or a DISTRICT
 * (area). Returned by GET /locations/search, ranked by active-listing count
 * (cities first). `id` is the exact City.id (kind='city') or Area.id
 * (kind='area') used for deterministic, 100%-accurate search filtering.
 */
export interface LocationSuggestion {
  kind: 'city' | 'area';
  id: string;
  nameEn: string;
  nameAr: string;
  slug: string;
  /** Parent city id (equals `id` for a city). */
  cityId: string;
  /** Parent city names (for the "District, City" label on areas). */
  cityNameEn: string | null;
  cityNameAr: string | null;
  /** Centroid — used for the optional "within N km" radius search. */
  latitude: number | null;
  longitude: number | null;
  activeListingCount: number;
  /** District score 0–5 (areas only; nightly job). Null = no chip shown. */
  score?: number | null;
}

/**
 * Combined city + district autocomplete by name (EN + AR). This is the
 * Rightmove-style resolver: the user picks a known location, and its exact
 * id (cityId/areaId) drives deterministic filtering. Optional `cityId` scopes
 * suggestions to that city's districts only.
 */
export async function searchLocations(q: string, cityId?: string, limit = 10): Promise<LocationSuggestion[]> {
  const params = new URLSearchParams({ q });
  if (cityId) params.set('cityId', cityId);
  if (limit) params.set('limit', String(limit));
  const res = await apiRequest<{ data: LocationSuggestion[] }>(`/locations/search?${params.toString()}`);
  return res.data ?? [];
}

export async function fetchAreaBoundaries(cityId?: string): Promise<AreaBoundary[]> {
  const q = cityId ? `?cityId=${encodeURIComponent(cityId)}` : '';
  const res = await apiRequest<{ data: AreaBoundary[] }>(`/locations/areas/boundaries${q}`);
  return res.data ?? [];
}

export async function fetchAreaInsights(areaId: string, period = '90d'): Promise<AreaInsights> {
  const res = await apiRequest<{ data: AreaInsights }>(`/locations/areas/${areaId}/insights?period=${period}`);
  return res.data;
}

/**
 * Colour for a district score — one source of truth for the map tint, the
 * legend, and score chips. Green (≥4) → teal (≥3.5) → amber (<3);
 * null → neutral grey (honest "no score yet").
 */
export function scoreTint(score: number | null | undefined): { fill: string; label: string } {
  if (score == null) return { fill: '#93A7A3', label: 'none' };  // gray-400
  if (score >= 4.0) return { fill: '#16a34a', label: 'high' };   // green-600
  if (score >= 3.5) return { fill: '#1B7566', label: 'good' };   // brand teal-500
  if (score >= 3.0) return { fill: '#DFA906', label: 'fair' };   // brand gold-500
  return { fill: '#B58A0A', label: 'low' };                      // brand gold-600
}

/** Convert a GeoJSON Polygon/MultiPolygon into google.maps polygon paths. */
export function geojsonToPaths(geometry: GeoJSONGeometry): google.maps.LatLngLiteral[][] {
  const rings: google.maps.LatLngLiteral[][] = [];
  const ringToPath = (ring: number[][]) => ring.map((c) => ({ lat: c[1], lng: c[0] }));
  if (geometry.type === 'Polygon') {
    (geometry.coordinates as number[][][]).forEach((ring) => rings.push(ringToPath(ring)));
  } else if (geometry.type === 'MultiPolygon') {
    (geometry.coordinates as number[][][][]).forEach((poly) => poly.forEach((ring) => rings.push(ringToPath(ring))));
  }
  return rings;
}
